import { formatCaseFacilityLocation, formatForDatePicker, formatForTimePicker } from '@features/forms/CaseForms/utils';
import { isNil } from 'lodash-es';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { getClinicalRecordByType, resolveUpdatesOrNull } from '@/utils/helpers';
import {
  AssignedUsersByGroup,
  Assignee,
  Case,
  DraftCase,
  CaseUserGroup,
  ClinicalItemType,
  Laterality,
  NO_COORDINATOR_ID,
  NRPOption,
  NRPType,
  ServiceType,
  TxCenterORInitialData,
} from '@/api/apis/case';
import { CustomerMT, CustomerRoleT, TxCustomer } from '@/api/apis/customer';
import { NA_REGION_ID } from '@/constants/serviceRegion.constants';
import { DEFAULT_TIMEZONE } from '@/constants/timezone';

export const getNrpOption = (caseNrpValue: boolean | undefined) => {
  if (isNil(caseNrpValue)) return undefined;
  if (caseNrpValue) return NRPOption.Yes;
  return NRPOption.No;
};

export const getCaseFormDefaultValues = (caseInfo: Case | DraftCase): CaseFormType => {
  const {
    donorId,
    donorType,
    serviceType,
    serviceDetails,
    organType,
    opoRequest,
    reAllocationCaseId,
    pediatricCase,
    files,
    links,
    donorHospitalTimeZone,
    donorHospitalDate,
    donorHospitalTime,
    assignedOpoContacts,
    clinicalData,
    txCenterDetails,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    donorHospitalDetails,
    assignedCustomers,
    opoDetails,
    isExpeditedCase,
    isServiceWithoutCoordinator,
    trialProgram,
    serviceRegionId,
    isQuestionaryEditAllowed,
    laterality,
  } = caseInfo ?? {};
  const opoContact = assignedOpoContacts?.find((c) => c.isPrimary);
  const secondaryOpoContact = assignedOpoContacts?.find((c) => !c.isPrimary);
  const nrpData = getClinicalRecordByType(clinicalData ?? [], ClinicalItemType.NRPApplicabilityAndType);
  const nrp = getNrpOption(nrpData?.item?.yesNo);
  const nrpType = nrpData?.item?.nrpType;
  const donorLocation = formatCaseFacilityLocation(donorHospitalDetails?.location);
  const txCenterLocation = formatCaseFacilityLocation(txCenterDetails?.location);
  const coordinator = assignedCustomers?.CustomerCoordinators?.find((c) => c.isDefaultTxUser);
  const surgeon = assignedCustomers?.CustomerReceivingSurgeons?.find((c) => c.isDefaultTxUser);

  return {
    isExpeditedCase: isExpeditedCase ?? false,
    donorId,
    donorType,
    serviceType,
    serviceDetails: serviceDetails ?? '',
    organType,
    opoRequest,
    originalOpoRequest: opoRequest,
    reAllocationCase: !!reAllocationCaseId,
    reAllocationCaseId,
    originalReAllocationCaseId: reAllocationCaseId,
    pediatricCase,
    files,
    links,
    donorHospitalId: donorHospitalDetails?.id || '',
    donorHospitalName: donorHospitalDetails?.name || '',
    hospitalCustomLocation: donorLocation,
    donorHospitalDate: formatForDatePicker(donorHospitalDate),
    donorHospitalTime: formatForTimePicker(donorHospitalTime),
    donorHospitalTimeZone: donorHospitalTimeZone || donorLocation.timezone || DEFAULT_TIMEZONE,
    opoId: opoDetails?.id || '',
    opoName: opoDetails?.name || '',
    opoContactId: opoContact?.id || '',
    opoSourceContactPhone: opoContact?.primaryPhone || '',
    secondaryOpoContactId: secondaryOpoContact?.id || '',
    secondaryOpoContactPhone: secondaryOpoContact?.primaryPhone || '',
    txCenterId: txCenterDetails?.id || '',
    txCenterName: txCenterDetails?.name || '',
    txCenterCustomLocation: txCenterLocation,
    txCenterTrialPrograms: txCenterDetails?.trialPrograms,
    recipientHospitalDate: formatForDatePicker(recipientHospitalDate),
    recipientHospitalTime: formatForTimePicker(recipientHospitalTime),
    recipientHospitalTimeZone: recipientHospitalTimeZone || txCenterLocation.timezone || DEFAULT_TIMEZONE,
    coordinatorId: coordinator?.customerId ?? (isServiceWithoutCoordinator ? NO_COORDINATOR_ID : ''),
    receivingSurgeonId: surgeon?.customerId ?? '',
    nrp,
    nrpType: !nrpType || nrpType === NRPType.none ? NRPType.aNrp : nrpType,
    trialProgram,
    serviceRegionId: serviceRegionId ?? NA_REGION_ID,
    isQuestionaryEditAllowed: isQuestionaryEditAllowed ?? false,
    laterality: laterality ?? Laterality.NotDefined,
    ...(isServiceWithoutCoordinator ? { isServiceWithoutCoordinator } : {}),
  };
};

export const formatOpoNameWithCode = (item?: { name?: string | null; code?: string | null } | null): string => {
  if (!item?.name) return '';

  return item.code ? `${item.code} - ${item.name}` : item.name;
};

export const removeOpoCodePrefix = (value: string) => {
  return value.includes(' - ') ? value.split(' - ').slice(1).join(' - ') : value;
};

type OpoSortable = { name?: string | null; code?: string | null };

const startsWithDigit = (value: string) => /^\d/.test(value);

export const compareOpoByCode = (a: OpoSortable, b: OpoSortable): number => {
  const keyA = a.code?.trim() || a.name?.trim() || '';
  const keyB = b.code?.trim() || b.name?.trim() || '';

  const isDigitA = startsWithDigit(keyA);
  const isDigitB = startsWithDigit(keyB);
  if (isDigitA !== isDigitB) return isDigitA ? 1 : -1;

  // `numeric` keeps "TX2" before "TX10"; `sensitivity: 'base'` keeps casing from splitting groups.
  return keyA.localeCompare(keyB, undefined, { numeric: true, sensitivity: 'base' });
};

export const getUpdatedAssignees = (
  prevAssignees: Assignee[],
  newId: string | null | undefined,
  group: CaseUserGroup,
): Assignee[] => {
  const normalizedNewId = newId && newId.length > 0 ? newId : null;
  const prevId =
    [...(prevAssignees ?? [])].reverse().find((a) => a.group === group && a.action !== 'Unassign')?.customerId ?? null;

  const actions: Assignee[] = [];

  if (normalizedNewId) {
    if (prevId && prevId !== normalizedNewId) {
      actions.push({ customerId: prevId, group, action: 'Unassign' });
    }
    if (prevId !== normalizedNewId) {
      actions.push({ customerId: normalizedNewId, group, action: 'Assign' });
    }
  } else if (prevId) {
    actions.push({ customerId: prevId, group, action: 'Unassign' });
  }

  return actions;
};

export const getTxAssigneePayload = (
  prevId: string | null | undefined,
  newId: string | null | undefined,
  group: CaseUserGroup,
) => {
  if (prevId === newId) return [];

  const actions: Assignee[] = [];

  if (prevId) {
    actions.push({ customerId: prevId, group, action: 'Unassign' });
  }

  if (newId) {
    actions.push({ customerId: newId, group, action: 'Assign' });
  }

  return actions;
};

type TxCenterContactValues = {
  coordinatorId?: string | null;
  receivingSurgeonId?: string | null;
  isServiceWithoutCoordinator?: boolean;
};

export const getTxAssigneesDiff = (prevValues: TxCenterContactValues, nextValues: TxCenterContactValues) => {
  const surgeonPayload = getTxAssigneePayload(
    prevValues.receivingSurgeonId,
    nextValues.receivingSurgeonId,
    CaseUserGroup.CustomerReceivingSurgeons,
  );

  const getCoordinatorId = (id: string | null | undefined) => (id === NO_COORDINATOR_ID ? null : id);
  const prevCoordinatorId = getCoordinatorId(prevValues.coordinatorId);
  const nextCoordinatorId = getCoordinatorId(nextValues.coordinatorId);
  const coordinatorPayload = getTxAssigneePayload(
    prevCoordinatorId,
    nextCoordinatorId,
    CaseUserGroup.CustomerCoordinators,
  );

  return [...surgeonPayload, ...coordinatorPayload];
};

export const getTXCenterORUpdatedValues = <T extends Partial<TxCenterORInitialData>>(
  updates: Partial<TxCenterORInitialData>,
  { recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone }: T,
) => {
  return resolveUpdatesOrNull(updates, { recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone }, [
    'recipientHospitalDate',
    'recipientHospitalTime',
    'recipientHospitalTimeZone',
  ]);
};

export const getTxCustomersByRole = (
  users: CustomerMT[] | undefined,
  role: CustomerRoleT,
  txCenterId?: string,
): TxCustomer[] => {
  if (!users || !txCenterId) return [];

  return users.reduce<TxCustomer[]>((acc, user) => {
    const center = user.customerInTxCenters.find((c) => c.txCenterId === txCenterId && c.roles?.includes(role));

    if (center) {
      acc.push({
        ...user,
        ...center,
      });
    }

    return acc;
  }, []);
};

export const cleanId = (v?: string | null): string | null => (v && v.length > 0 ? v : null);

export const makePrevDefaultAssignees = (caseInfo: Pick<Case, 'assignedCustomers'>): Assignee[] => {
  const prevSurgeonId =
    caseInfo.assignedCustomers?.CustomerReceivingSurgeons?.find((c) => c.isDefaultTxUser)?.customerId ?? null;

  const prevCoordinatorId =
    caseInfo.assignedCustomers?.CustomerCoordinators?.find((c) => c.isDefaultTxUser)?.customerId ?? null;

  return [
    ...(prevSurgeonId
      ? [{ customerId: prevSurgeonId, group: CaseUserGroup.CustomerReceivingSurgeons, action: 'Assign' as const }]
      : []),
    ...(prevCoordinatorId
      ? [{ customerId: prevCoordinatorId, group: CaseUserGroup.CustomerCoordinators, action: 'Assign' as const }]
      : []),
  ];
};

export const buildUnassignTxUsersIfDA = (
  prevServiceType: ServiceType,
  nextServiceType: ServiceType,
  assignedCustomers: AssignedUsersByGroup | undefined,
): Assignee[] => {
  if (prevServiceType === ServiceType.DirectAcquisition || nextServiceType !== ServiceType.DirectAcquisition) {
    return [];
  }

  const res: Assignee[] = [];
  const pushUnassign = (group: CaseUserGroup) => {
    (assignedCustomers?.[group] ?? []).forEach((a) => {
      const id = a?.customerId ?? a?.user?.id;
      if (id) res.push({ group, customerId: id, action: 'Unassign' });
    });
  };

  pushUnassign(CaseUserGroup.CustomerCoordinators);
  pushUnassign(CaseUserGroup.CustomerReceivingSurgeons);

  return res;
};

const isValidAssign = (a: Assignee): a is Assignee & { customerId: string; group: CaseUserGroup; action: 'Assign' } =>
  !!a.customerId && !!a.group && a.action === 'Assign';

export const buildDefaultTxUnassign = (assignedCustomers: Case['assignedCustomers']): Assignee[] => {
  const prev = makePrevDefaultAssignees({ assignedCustomers });
  return prev.filter(isValidAssign).map(({ customerId, group }) => ({
    customerId,
    group,
    action: 'Unassign',
  }));
};
